import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiChevronLeft, FiChevronRight, FiDownload, FiTrash2, FiEdit2, FiCheck } from 'react-icons/fi';
import { format } from 'date-fns';
import { photoService } from '../../services/api';
import './PhotoLightbox.css';

const PhotoLightbox = ({ photo, onClose, onPrev, onNext, hasPrev, hasNext, onDelete, onCaptionUpdated }) => {
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionText, setCaptionText] = useState(photo.caption || '');
  const [displayCaption, setDisplayCaption] = useState(photo.caption || '');
  const [savingCaption, setSavingCaption] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const [zoomed, setZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState('center center');
  const lastTapRef = useRef(0);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const panStartRef = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    if (zoomed) {
      panStartRef.current = {
        touchX: e.touches[0].clientX,
        touchY: e.touches[0].clientY,
        offsetX: panOffset.x,
        offsetY: panOffset.y
      };
    }
  };

  const handleTouchMove = (e) => {
    if (zoomed && panStartRef.current && e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - panStartRef.current.touchX;
      const deltaY = e.touches[0].clientY - panStartRef.current.touchY;
      setPanOffset({
        x: panStartRef.current.offsetX + deltaX,
        y: panStartRef.current.offsetY + deltaY
      });
    }
  };

  const handleTouchEnd = (e) => {
    panStartRef.current = null;
    if (touchStartX.current === null) return;

    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;

    if (!zoomed && photo.fileType !== 'video' && Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      if (deltaX < 0 && hasNext) onNext();
      if (deltaX > 0 && hasPrev) onPrev();
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  const handleImageTap = (e) => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      e.preventDefault();
      e.stopPropagation();
      if (zoomed) {
        setZoomed(false);
        setPanOffset({ x: 0, y: 0 });
      } else {
        const rect = e.target.getBoundingClientRect();
        const x = ((e.changedTouches ? e.changedTouches[0].clientX : e.clientX) - rect.left) / rect.width * 100;
        const y = ((e.changedTouches ? e.changedTouches[0].clientY : e.clientY) - rect.top) / rect.height * 100;
        setZoomOrigin(`${x}% ${y}%`);
        setZoomed(true);
      }
      lastTapRef.current = 0;
      return;
    }
    lastTapRef.current = now;
  };
  
  useEffect(() => {
    setCaptionText(photo.caption || '');
    setDisplayCaption(photo.caption || '');
    setEditingCaption(false);
    setZoomed(false);
    setPanOffset({ x: 0, y: 0 });
  }, [photo]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editingCaption) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext, hasPrev, hasNext, editingCaption]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = photo.fileURL;
    link.download = photo.fileName || 'photo';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      onDelete(photo.photoID);
    }
  };

  const handleSaveCaption = async () => {
    try {
      setSavingCaption(true);
      const response = await photoService.updatePhoto(photo.photoID, { caption: captionText });
      if (response.success) {
        setDisplayCaption(captionText);
        setEditingCaption(false);
        if (onCaptionUpdated) {
          onCaptionUpdated(photo.photoID, captionText);
        }
      }
    } catch (err) {
      console.error('Failed to update caption:', err);
    } finally {
      setSavingCaption(false);
    }
  };

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-scroll-container" onClick={onClose}>
        {/* Header */}
        <div className="lightbox-header" onClick={e => e.stopPropagation()}>
          <div className="lightbox-actions">
            <button className="lightbox-btn" onClick={handleDownload} title="Download">
              <FiDownload />
            </button>
            {onDelete && (
              <button className="lightbox-btn lightbox-btn-delete" onClick={handleDelete} title="Delete">
                <FiTrash2 />
              </button>
            )}
            <button className="lightbox-btn" onClick={onClose} title="Close">
              <FiX />
            </button>
          </div>
        </div>

        {/* Image - full screen */}
        <div 
          className="lightbox-content" 
          onClick={e => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {photo.fileType === 'video' ? (
            <video
              src={photo.fileURL}
              className="lightbox-image"
              controls
              playsInline
              poster={photo.thumbnailURL || undefined}
            />
          ) : (
            <img 
              src={photo.fileURL} 
              alt={displayCaption || 'Photo'} 
              className={`lightbox-image ${zoomed ? 'zoomed' : ''}`}
              style={zoomed ? { transformOrigin: zoomOrigin, transform: `scale(2.5) translate(${panOffset.x / 2.5}px, ${panOffset.y / 2.5}px)` } : undefined}
              onTouchEnd={handleImageTap}
              onDoubleClick={handleImageTap}
            />
          )}
        </div>

        {/* Caption and details below image */}
        <div className="lightbox-details" onClick={e => e.stopPropagation()}>
          <div className="lightbox-caption-section">
            {editingCaption ? (
              <div className="lightbox-caption-edit">
                <input
                  type="text"
                  className="lightbox-caption-input"
                  value={captionText}
                  onChange={(e) => setCaptionText(e.target.value)}
                  placeholder="Add a caption..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveCaption();
                    if (e.key === 'Escape') { setEditingCaption(false); setCaptionText(displayCaption); }
                  }}
                />
                <button className="lightbox-btn" onClick={handleSaveCaption} disabled={savingCaption} title="Save">
                  <FiCheck />
                </button>
                <button className="lightbox-btn" onClick={() => { setEditingCaption(false); setCaptionText(displayCaption); }} title="Cancel">
                  <FiX />
                </button>
              </div>
            ) : (
              <div className="lightbox-caption-display">
                <span className="lightbox-caption-text">
                  {displayCaption || 'No caption'}
                </span>
                {onDelete && (
                  <button className="lightbox-btn lightbox-caption-edit-btn" onClick={() => setEditingCaption(true)} title="Edit caption">
                    <FiEdit2 />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="lightbox-meta">
            {photo.takenDate && (
              <span className="lightbox-date">
                Date Taken: {format(new Date(photo.takenDate.split('T')[0] + 'T12:00:00'), 'MMM d, yyyy')}
              </span>
            )}
            {photo.member?.fullName && (
              <span className="lightbox-date">
                Uploaded By: {photo.member.fullName}
              </span>
            )}
            {photo.createdDate && (
              <span className="lightbox-date">
                Date Uploaded: {format(new Date(photo.createdDate + 'Z'), 'MMM d, yyyy')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      {hasPrev && (
        <button 
          className="lightbox-nav lightbox-prev" 
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          title="Previous"
        >
          <FiChevronLeft />
        </button>
      )}
      {hasNext && (
        <button 
          className="lightbox-nav lightbox-next" 
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          title="Next"
        >
          <FiChevronRight />
        </button>
      )}
    </div>
  );
};

export default PhotoLightbox;